import React, { createContext, useContext } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

// Context to pass drag listeners to drag handle
const DragHandleContext = createContext(null)

export function SortableRow({
  id,
  children,
  onMouseDown,
  isInvalidDropZone = false,
  isDraggingStep = false,
  isLastStep = false,
  isCollapsedTestcase = false
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })
  
  // Capture and maintain original height when dragging
  const rowRef = React.useRef(null)
  const [originalHeight, setOriginalHeight] = React.useState(null)
  
  React.useEffect(() => {
    if (rowRef.current && !isDragging && originalHeight === null) {
      // Capture original height when not dragging
      setOriginalHeight(rowRef.current.offsetHeight)
    }
  }, [isDragging, originalHeight])
  
  // Reset height when drag ends
  React.useEffect(() => {
    if (!isDragging && originalHeight !== null) {
      // Reset after a brief delay to allow transition
      const timer = setTimeout(() => {
        setOriginalHeight(null)
      }, 300)
      return () => clearTimeout(timer)
    }
  }, [isDragging, originalHeight])

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1, // More faded when dragging since DragOverlay card is the primary visual indicator
    ...(isDragging && originalHeight ? { height: `${originalHeight}px`, minHeight: `${originalHeight}px`, maxHeight: `${originalHeight}px` } : {})
  }
  
  // Remove opacity change - we'll use subtle background instead

  // Handle mouse down - check if clicking on drag handle
  const handleRowMouseDown = (e) => {
    // If clicking on drag handle, don't interfere - let dnd-kit handle it
    if (e.target.closest('.drag-handle')) {
      return // Don't call onMouseDown, but don't stop propagation either
    }
    // Otherwise, call the parent's onMouseDown for click-to-edit
    if (onMouseDown) {
      onMouseDown(e)
    }
  }

  // Build className with drag state classes
  let className = 'test-row'

  // Add step-row class for step rows (for background styling)
  if (id.startsWith('step-')) {
    className += ' step-row'
  }

  if (isInvalidDropZone) {
    className += ' invalid-drop-zone'
  }
  if (isDraggingStep && id.startsWith('step-')) {
    className += ' dragging-step'
  }

  // Combine refs to capture height and set sortable ref
  const combinedRef = React.useCallback((node) => {
    rowRef.current = node
    setNodeRef(node)
  }, [setNodeRef])

  return (
    <DragHandleContext.Provider value={{ listeners, attributes }}>
      <tr
        ref={combinedRef}
        style={style}
        onMouseDown={handleRowMouseDown}
        className={className}
        data-row-id={id}
        data-dragging={isDragging ? "true" : "false"}
        data-is-last-step={isLastStep ? "true" : undefined}
        data-is-collapsed-testcase={isCollapsedTestcase ? "true" : undefined}
      >
        {children}
      </tr>
    </DragHandleContext.Provider>
  )
}

// Hook to get drag handle listeners
export function useDragHandle() {
  return useContext(DragHandleContext)
}

